import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  fsDownload,
  fsListDir,
  type FsItem,
  type FsKind,
} from '../transport/api'
import { Icon, type IconName } from '../ui/Icon'
import { formatBytes as human } from '../ui/bytes'
import { useFsActions } from './useFsActions'
import { useCtxMenu } from './useCtxMenu'
import { useGitDock } from './useGitDock'
import { RenameSheet } from './RenameSheet'
import { UploadConflictSheet } from './UploadConflictSheet'
import { downloadBlob } from '../ui/blobUrl'
import { copyText } from '../ui/clipboard'
import { GitPanel } from '../git/GitPanel'

/**
 * The right-side file browser drawer.
 *
 * Docks rather than overlays: it sets `--fs-dock`, which #bar / #term / #status /
 * #footbar / #projPanel all subtract from their `right`, so the panes shrink instead of
 * being covered. That is how the original behaves and it matters for the terminal —
 * xterm refits to the narrower box rather than having its right-hand columns hidden.
 */

const KIND_ICON: Record<string, IconName> = {
  image: 'open_in_new',
  video: 'open_in_new',
  audio: 'open_in_new',
  markdown: 'content_paste',
  text: 'content_paste',
  other: 'content_paste',
}

export function FileBrowser({
  session,
  host,
  onOpenFile,
  onClose,
  onStatus,
  gitView,
  onGitView,
}: {
  /** When set, an empty path resolves to THIS agent's active tmux pane directory. */
  session?: string | null
  /** The agent's host. Without it a remote agent's browser silently shows Origin. */
  host?: string | null
  onOpenFile: (f: { path: string; name: string; kind: FsKind; size: number | null | undefined }) => void
  onClose: () => void
  onStatus: (msg: string) => void
  /**
   * Show Source Control for the folder on screen, in place of the listing.
   *
   *   owner: 把 Side panel 上面的 git 按鈕搬到右側 file browser 底部並且啟用時
   *          直接讓該 folder 的 git 出現在 file browser 頁面裡
   *
   * CONTROLLED from App, not local state, because ⌘⇧G still has to reach it — the
   * shortcut opens the drawer and this view together, and a second source of truth
   * would let the two disagree about which one is showing.
   */
  gitView: boolean
  onGitView: (on: boolean) => void
}) {
  // null = "let the server decide" (the agent's pane cwd). Adopted from the response.
  const [path, setPath] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  // Mutations must target the directory actually on screen, including when it came
  // from the server's default rather than from `path`.
  const cwdRef = useRef('~')
  // The listing as it is RIGHT NOW, for the upload collision check — a callback built
  // renders ago must not decide "that name is free" from a folder that has changed.
  const entriesRef = useRef<FsItem[]>([])
  /** The entry the rename sheet is open for; null when it is closed. */
  const [renaming, setRenaming] = useState<{ path: string; name: string; isDir: boolean } | null>(
    null,
  )

  const listing = useQuery({
    queryKey: ['fs', path, session ?? null, host ?? null],
    queryFn: () => fsListDir(path, path ? null : session, host),
  })

  /*
   * M6 (拆面板): the browser's four stateful clusters live in four hooks now —
   * mutations+selection, the cd button, the context menu, and the drawer geometry.
   * Each was a verbatim move; this component keeps navigation, the listing, and JSX.
   */
  const {
    mkdir, upload, remove, rename, refresh, uploadPct,
    mkdirOpen, setMkdirOpen, newName, setNewName,
    selectMode, setSelectMode, picked, setPicked,
    startUpload, pending, resolveUpload,
  } = useFsActions(cwdRef, host, entriesRef)
  const { ctx, setCtx, ctxHold, ctxFired, ctxStart, cancelHold, openCtx } = useCtxMenu(path)
  const { gitFrac, dockRef, startDrag } = useGitDock()

  /** Shared by the path-bar button and the per-entry context menu. The decision lives
   *  in ui/clipboard.ts; what is local is HOW this surface says it — a status line, and
   *  when the write could not happen, the text itself so it can be selected by hand. */
  const copyPathText = async (text: string) => {
    const r = await copyText(text)
    onStatus(r.ok ? `path copied ✓ — ${text}` : `copy manually: ${text}`)
  }

  const doCopyPath = async () => {
    const path = cwdRef.current
    if (!path) {
      onStatus('no folder open')
      return
    }
    await copyPathText(path)
  }

  const cwd = listing.data?.path ?? path ?? '~'
  /*
   * The folder Source Control may ask about — null until one is actually KNOWN.
   *
   * `cwd` falls back to '~' so the path bar has something to print on the first frame,
   * and handing that to git meant the panel's first request asked about the home
   * directory: usually not a repo, so opening the view flashed "not a git repository"
   * before the real answer, and a slow listing would leave that on screen. `~` is a
   * placeholder for display, not an answer to "which directory am I in".
   */
  const gitFolder = listing.data?.path ?? path
  const err = listing.data?.error ?? (listing.error ? String(listing.error) : null)
  // The rename sheet shows its own error in place, beside the name still in the field;
  // repeating it up here would say the same thing twice, in the wrong place.
  const mutErr = mkdir.error ?? upload.error ?? remove.error

  cwdRef.current = cwd
  entriesRef.current = listing.data?.entries ?? []

  // Selection belongs to the folder on screen. `setPicked` is a setState passed
  // through a hook, so eslint cannot see its stability — listed to keep the
  // ratchet at zero; identity-stable, so the effect still fires only on cwd.
  useEffect(() => {
    setPicked(new Set())
  }, [cwd, setPicked])

  const open = (e: FsItem) => {
    const full = `${cwd.replace(/\/$/, '')}/${e.name}`
    if (e.type === 'dir') {
      setPath(full)
    } else {
      onOpenFile({ path: full, name: e.name, kind: e.kind ?? 'other', size: e.size })
    }
  }

  return (
    <aside
      id="fsDock"
      ref={dockRef}
      style={{ ['--fs-git-frac' as string]: gitView ? String(gitFrac) : '0' }}
      className={dragging ? 'dropping' : undefined}
      aria-label="File browser"
      onDragOver={(e) => {
        // Must preventDefault on BOTH dragover and drop, or the browser navigates to
        // the dropped file instead of handing it over.
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'copy'
          setDragging(true)
        }
      }}
      onDragLeave={(e) => {
        // Fires for children too, so only clear when the pointer truly left the drawer.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        /*
         * THIS DRAWER OWNS ITS DROPS — owner, 2026-09-14: 在 webapp / Mac app 裡面拖曳
         * 檔案上傳到 file browser 居然也會跑進去 Text edit 裡面.
         *
         * App.tsx listens for `drop` on the WINDOW so that the terminal and every other
         * view accept files into the composer. Without stopPropagation that handler
         * runs too, and one drop aimed at a folder was uploaded here AND attached to
         * the agent's message composer — the second destination silent and unasked for.
         *
         * NotePanel and ProjectPanel already do exactly this line, for exactly this
         * reason, and their comments say so. This drawer was the one surface that
         * accepted files without claiming them.
         */
        e.stopPropagation()
        setDragging(false)
        // startUpload, not upload.mutate: it asks about names already taken before a
        // single byte moves. Both entrances go through it — a drop and the Upload
        // button must not answer the collision question differently.
        startUpload([...e.dataTransfer.files])
      }}
    >
      <div className="fs-hd">
        <button
          type="button"
          className="fs-icon"
          title="Up one folder"
          aria-label="Up one folder"
          disabled={!listing.data?.parent}
          onClick={() => {
            const p = listing.data?.parent
            if (p) setPath(p)
          }}
        >
          <Icon name="arrow_upward" size={16} label="Up one folder" />
        </button>
        <span className="fs-path" title={cwd}>
          {cwd}
        </span>
        {/* Both act on the LISTING. Source Control has its own refresh in its header,
            and there is nothing here to tick a checkbox against, so leaving them would
            be two buttons whose effect is off-screen. "Up one folder" stays: navigating
            is how you change which repo is shown. */}
        {!gitView && (
          <>
            <button
              type="button"
              className={`fs-icon${selectMode ? ' on' : ''}`}
              title="Select items to delete"
              aria-label="Select items to delete"
              aria-pressed={selectMode}
              onClick={() => setSelectMode((v) => !v)}
            >
              <Icon name="check_box" size={16} label="Select items" />
            </button>
            <button
              type="button"
              className="fs-icon"
              title="Refresh"
              aria-label="Refresh"
              onClick={refresh}
            >
              <Icon name="refresh" size={16} label="Refresh" />
            </button>
          </>
        )}
        <button type="button" className="fs-icon" title="Close" aria-label="Close" onClick={onClose}>
          <Icon name="close" size={16} label="Close" />
        </button>
      </div>

      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          startUpload([...(e.target.files ?? [])])
          e.target.value = ''
        }}
      />

      {mkdirOpen && (
        <form
          className="fs-mkdir"
          onSubmit={(e) => {
            e.preventDefault()
            if (newName.trim()) mkdir.mutate()
          }}
        >
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="new folder name"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            // autofocus is deliberate: the rename field opens for typing (fossil eslint-disable removed 2026-09-02)
            autoFocus
          />
          <button type="submit" disabled={!newName.trim() || mkdir.isPending}>
            Create
          </button>
        </form>
      )}

      {upload.isPending && (
        <div className="fs-note">uploading…{uploadPct !== null ? ` ${uploadPct}%` : ''}</div>
      )}
      {mutErr && <div className="fs-note fs-err">{String(mutErr)}</div>}
      {err && <div className="fs-note fs-err">{err}</div>}
      {listing.data?.fallback_from && (
        <div className="fs-note">
          this agent’s folder is gone — showing home instead (was {listing.data.fallback_from})
        </div>
      )}
      {listing.data?.truncated && (
        <div className="fs-note">showing first 2000 entries — folder is larger</div>
      )}

    <div className="fs-list" role="list">
      {listing.isPending && <div className="fs-note">loading…</div>}
      {listing.data?.entries.length === 0 && <div className="fs-note">empty folder</div>}

      {listing.data?.entries.map((e) => {
        const full = `${cwd.replace(/\/$/, '')}/${e.name}`
        const isDir = e.type === 'dir'
        return (
          <div className="fs-row" role="listitem" key={e.name}>
            <button
              type="button"
              className="fs-open"
              onContextMenu={(ev) => {
                // Right-click is the desktop long-press; eat the native menu
                // (which mobile also raises on a long touch).
                ev.preventDefault()
                cancelHold()
                openCtx(ev.clientX, ev.clientY, full, e.name, isDir)
              }}
              onPointerDown={(ev) => {
                if (ev.pointerType === 'mouse' && ev.button !== 0) return
                ctxFired.current = false
                ctxStart.current = { x: ev.clientX, y: ev.clientY }
                const { clientX, clientY } = ev
                cancelHold()
                ctxHold.current = window.setTimeout(() => {
                  ctxFired.current = true
                  openCtx(clientX, clientY, full, e.name, isDir)
                }, 500)
              }}
              onPointerMove={(ev) => {
                // Wandered past the slop: this is a scroll, not a press.
                if (
                  Math.abs(ev.clientX - ctxStart.current.x) > 8 ||
                  Math.abs(ev.clientY - ctxStart.current.y) > 8
                )
                  cancelHold()
              }}
              onPointerUp={cancelHold}
              onPointerLeave={cancelHold}
              onPointerCancel={cancelHold}
              onClick={() => {
                if (ctxFired.current) {
                  // The click that follows a long-press belongs to the hold.
                  ctxFired.current = false
                  return
                }
                if (!selectMode) {
                  open(e)
                  return
                }
                setPicked((prev) => {
                  const next = new Set(prev)
                  if (next.has(full)) next.delete(full)
                  else next.add(full)
                  return next
                })
              }}
              title={selectMode ? `Select ${e.name}` : isDir ? 'Open folder' : e.name}
            >
              {selectMode && (
                <Icon name={picked.has(full) ? 'check_box' : 'add'} size={15} />
              )}
              <Icon
                name={isDir ? 'folder' : (KIND_ICON[e.kind ?? 'other'] ?? 'content_paste')}
                size={15}
              />
              <span className="fs-name">{e.name}</span>
              {!isDir && <span className="fs-size">{human(e.size)}</span>}
            </button>

            {/*
              * The per-row "open in a new tab" is GONE (owner: 那個按鈕沒什麼用).
              *
              * It pointed at `/fs/file`, which this client cannot reach, so the tab it
              * opened showed `{"error":"no_such_route"}`. Not merely redundant beside a
              * viewer that opens the same file — a button whose only outcome was an error
              * page. Tapping the row is the one way in, and it works.
              */}
          </div>
        )
      })}
    </div>

      {/*
        * Source Control opens as a BOTTOM HALF, sharing the height with the listing.
        *
        *   owner: 讓 file browser 的 git 從下方半開,與瀏覽檔案的畫面對分垂直空間
        *          (可調整). 當使用者 navigate 到不同的 folder path 則 git 的部分
        *          會即時更新狀態.
        *
        * It used to REPLACE the listing, which made the path bar the only thing
        * connecting the two — you could see the repo or the folder it came from,
        * never both. Splitting them is what makes navigating while it is open mean
        * anything: walk into a checkout and watch the changes below you change.
        *
        * NOT keyed on the folder any more. A key remounted the panel on every step,
        * which threw away a half-typed commit message for walking into a
        * subdirectory of the same repo. GitPanel already keys its queries on
        * `folder`, so the state follows the path without the component dying; what
        * genuinely must not survive a move — the open diff, the message — is cleared
        * there, on the repo changing rather than on the path changing.
        */}
      {ctx && (
        <>
          {/* A transparent shield: any press outside the menu closes it and goes
              no further — the row underneath must not open a file as a side
              effect of dismissing. */}
          <div
            className="fs-ctxshield"
            onPointerDown={(ev) => {
              ev.preventDefault()
              ev.stopPropagation()
              setCtx(null)
            }}
            onContextMenu={(ev) => {
              ev.preventDefault()
              setCtx(null)
            }}
          />
          <div
            className="fs-ctxmenu"
            role="menu"
            aria-label={`Actions for ${ctx.name}`}
            style={{ left: ctx.x, top: ctx.y }}
          >
            <div className="fs-ctxname" title={ctx.path}>
              {ctx.name}
            </div>
            <button
              type="button"
              role="menuitem"
              className="fs-ctxitem"
              onClick={() => {
                void copyPathText(ctx.path)
                setCtx(null)
              }}
            >
              <Icon name="content_paste" size={14} /> Copy path
            </button>
            {/*
              * owner, 2026-09-14, relaying a user: 我可以直接对我文件夹的下面的东西
              * 进行移动删除这种操作 → 在 file browser 裡面新增長按/右鍵彈出選單,
              * 裡面有 rename file 的功能.
              *
              * Offered for folders too. The edge operation is one rename either way,
              * and a menu that renames files but not the folder above them would be a
              * rule the person has to discover by it not working.
              */}
            <button
              type="button"
              role="menuitem"
              className="fs-ctxitem"
              onClick={() => {
                setRenaming({ path: ctx.path, name: ctx.name, isDir: ctx.isDir })
                rename.reset()
                setCtx(null)
              }}
            >
              <Icon name="edit" size={14} /> Rename
            </button>
            {!ctx.isDir && (
              /*
               * owner, 2026-09-02: 右鍵點擊 (或是使用者長按) 在彈出視窗裡面有
               * "Download file" 的功能. Bytes page across the relay (fsDownload),
               * then the shared downloadBlob hands them to the browser — the same
               * two rules every download in this app follows: progress in words
               * while it moves, and the failure said as a sentence when it does
               * not. A folder shows no item rather than a disabled one: "you
               * cannot download a folder" is better said by absence.
               */
              <button
                type="button"
                role="menuitem"
                className="fs-ctxitem"
                onClick={() => {
                  const { path: dlPath, name: dlName } = ctx
                  setCtx(null)
                  onStatus(`downloading ${dlName}…`)
                  fsDownload(dlPath, host, (pct) => onStatus(`downloading ${dlName}… ${pct}%`))
                    .then((blob) => {
                      downloadBlob(blob, dlName)
                      onStatus(`downloaded ${dlName} ✓`)
                    })
                    .catch((e) => onStatus(`download failed: ${String((e as Error).message ?? e)}`))
                }}
              >
                <Icon name="arrow_downward" size={14} /> Download file
              </button>
            )}
          </div>
        </>
      )}

      {renaming && (
        <RenameSheet
          name={renaming.name}
          isDir={renaming.isDir}
          host={host ?? null}
          busy={rename.isPending}
          error={rename.error ? String((rename.error as Error).message ?? rename.error) : null}
          onCancel={() => setRenaming(null)}
          onRename={(to) =>
            // Closed only on SUCCESS: "already exists" is an answer to the name still
            // in the field, and a sheet that vanished would take the question with it.
            rename.mutate(
              { path: renaming.path, to },
              {
                onSuccess: () => {
                  setRenaming(null)
                  onStatus(`renamed to ${to} ✓`)
                },
              },
            )
          }
        />
      )}

      {pending && (
        <UploadConflictSheet
          clashes={pending.clashes}
          host={host ?? null}
          onCancel={() => resolveUpload('cancel')}
          onKeepBoth={() => resolveUpload('keep')}
          onReplace={() => resolveUpload('replace')}
        />
      )}

      {gitView && gitFolder && (
        <>
          <div
            className="fs-split"
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize Source Control"
            onPointerDown={startDrag}
          />
          <GitPanel
            session={session ?? null}
            host={host ?? null}
            folder={gitFolder}
          />
        </>
      )}

      {selectMode ? (
        <div id="fsSelFoot">
          <button
            type="button"
            className="fsf ghost"
            onClick={() => {
              setSelectMode(false)
              setPicked(new Set())
            }}
          >
            <Icon name="close" size={14} /> Cancel
          </button>
          <button
            type="button"
            className="fsf danger"
            disabled={picked.size === 0 || remove.isPending}
            onClick={() => remove.mutate([...picked])}
          >
            <Icon name="delete" size={14} />{' '}
            {remove.isPending ? 'Deleting…' : `Delete (${picked.size})`}
          </button>
        </div>
      ) : (
        <div id="fsFoot">
          <button
            type="button"
            className="fsf up"
            title="Upload files to this folder"
            aria-label="Upload files to this folder"
            onClick={() => fileInput.current?.click()}
          >
            <Icon name="arrow_upward" size={19} />
            <span className="fsf-lbl">Upload</span>
          </button>
          <button
            type="button"
            className="fsf mk"
            title="Create a new subfolder here"
            aria-label="Create a new subfolder here"
            aria-expanded={mkdirOpen}
            onClick={() => setMkdirOpen((v) => !v)}
          >
            <Icon name="add" size={19} />
            <span className="fsf-lbl">New folder</span>
          </button>
          <button
            type="button"
            className="fsf cp"
            title="Copy this folder’s full path"
            aria-label="Copy this folder’s full path"
            onClick={() => void doCopyPath()}
          >
            <Icon name="content_paste" size={19} />
            <span className="fsf-lbl">Copy path</span>
          </button>
          {/*
            * Source Control, moved here from the sidebar header (owner). It belongs
            * beside the other things that act on THIS folder — up there it sat next to
            * the machine name and the theme toggle, which are about the server, and it
            * silently resolved its repo from the agent's pane instead of from anything
            * on screen. The path bar at the top of this drawer is now the repo
            * selector, which is a thing you can see and steer.
            */}
          <button
            type="button"
            className={`fsf git${gitView ? ' on' : ''}`}
            title={gitView ? 'Back to files' : 'Source Control for this folder (⌘⇧G)'}
            aria-label="Source Control for this folder"
            aria-pressed={gitView}
            onClick={() => onGitView(!gitView)}
          >
            <Icon name="call_split" size={19} />
            <span className="fsf-lbl">Git</span>
          </button>
        </div>
      )}
    </aside>
  )
}
